// src/tabs/logements/AddPoiModal.jsx — PR-FIX-4c (ADR-161)
//
// Modale "Ajouter manuellement un POI" depuis l'éditeur admin Aux Alentours.
//
// Workflow :
//   1. Onglet "🔍 Rechercher Google Places" :
//      - Input nom/adresse + bouton "Rechercher"
//      - Call `livret_alentours_places_search` (debounce manuel, pas auto)
//      - Affiche jusqu'à 5 candidats cliquables
//      - Click candidat → pré-remplit form (name, address, place_id, lat/lng,
//        rating, user_ratings_total, photo_reference)
//   2. Onglet "✏️ Saisie libre" :
//      - Saisie nom + adresse + (optionnel) lat, lng, website, phone (transports
//        D9 acté Tisséo libre).
//   3. Bouton "Valider l'ajout" → call `livret_alentours_poi_add_manual`
//      avec poi sanitisé (whitelist champs autorisés côté serveur).
//
// Rate limit feedback : si 429 sur recherche, affiche message + retry-after.
//
// a11y :
//   - Esc ferme la modale (handler clavier).
//   - role="dialog" + aria-modal="true" + aria-labelledby.
//   - aria-live="polite" pour résultats recherche.
//
// XSS sanitize :
//   - `escapeHtml` non requis (React échappe natif via {value}).
//   - Inputs limités en taille (200 chars name, 300 address, 500 URLs).
//
// `key` = catégorie cible (ex. "restaurants_bars", "transports"). Le payload
// envoyé respecte la whitelist serveur (cf. handleLivretAlentoursPoiAddManual).

import { useState, useEffect, useCallback } from "react";
import { DS } from "../../lib/ui";

async function _apiCall(action, { method = "POST", body, headers } = {}) {
  const url = `/api/beds24-mapping?action=${action}`;
  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({ error: "parse_fail" }));
  return { ok: res.ok, status: res.status, json };
}

export default function AddPoiModal({
  logement_id,
  sectionKey,
  getAuthHeaders,
  onClose,
  onAdded,
}) {
  // Mode = "search" | "manual"
  const [mode, setMode] = useState(
    sectionKey === "transports" ? "manual" : "search",
  );
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [searchError, setSearchError] = useState(null);
  const [retryAfter, setRetryAfter] = useState(0);

  // Form fields (pré-remplis ou saisis)
  const [form, setForm] = useState({
    name: "",
    address: "",
    place_id: "",
    lat: "",
    lng: "",
    rating: "",
    user_ratings_total: "",
    photo_reference: "",
    website: "",
    phone: "",
    tisseo_url: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // ── Esc handler + retry-after countdown ─────────────────────────────────
  useEffect(() => {
    function onEsc(e) {
      if (e.key === "Escape") onClose?.();
    }
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  useEffect(() => {
    if (retryAfter <= 0) return undefined;
    const t = setTimeout(() => setRetryAfter((n) => Math.max(0, n - 1)), 1000);
    return () => clearTimeout(t);
  }, [retryAfter]);

  // ── Recherche Google Places ─────────────────────────────────────────────
  const handleSearch = useCallback(
    async (e) => {
      e?.preventDefault?.();
      const q = query.trim();
      if (!q) {
        setSearchError("Saisir un terme de recherche");
        return;
      }
      if (q.length > 200) {
        setSearchError("Requête trop longue (max 200 caractères)");
        return;
      }
      setSearching(true);
      setSearchError(null);
      setCandidates([]);
      try {
        const headers = getAuthHeaders ? await getAuthHeaders() : {};
        const { ok, status, json } = await _apiCall(
          "livret_alentours_places_search",
          {
            method: "POST",
            body: { query: q, logement_id },
            headers,
          },
        );
        if (!ok) {
          if (status === 429) {
            const ra = Number(json.retry_after_s) || 60;
            setRetryAfter(ra);
            setSearchError(
              `Trop de recherches récentes. Réessayez dans ${ra}s.`,
            );
          } else if (status === 400 && json.error === "no_coordinates") {
            setSearchError(
              "Coordonnées du logement manquantes. Renseigner weather_lat/lng dans Infos avant.",
            );
          } else {
            setSearchError(json.message || `HTTP ${status}`);
          }
          return;
        }
        setCandidates(Array.isArray(json.candidates) ? json.candidates : []);
        if ((json.candidates || []).length === 0) {
          setSearchError("Aucun résultat trouvé.");
        }
      } catch (err) {
        setSearchError(err.message || "Erreur réseau");
      } finally {
        setSearching(false);
      }
    },
    [query, logement_id, getAuthHeaders],
  );

  // ── Sélectionne un candidat (pré-remplit form) ──────────────────────────
  function handleSelectCandidate(c) {
    setForm({
      name: (c.name || "").slice(0, 200),
      address: (c.address || "").slice(0, 300),
      place_id: c.place_id || "",
      lat: typeof c.lat === "number" ? String(c.lat) : "",
      lng: typeof c.lng === "number" ? String(c.lng) : "",
      rating: typeof c.rating === "number" ? String(c.rating) : "",
      user_ratings_total:
        typeof c.user_ratings_total === "number"
          ? String(c.user_ratings_total)
          : "",
      photo_reference: c.photo_reference || "",
      website: "",
      phone: "",
      tisseo_url: "",
    });
    setMode("manual"); // bascule en saisie pour permettre revue avant validation
  }

  // ── Valide l'ajout ──────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e?.preventDefault?.();
    const name = form.name.trim();
    if (!name) {
      setSubmitError("Le nom est requis");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Construction payload sanitisé (whitelist côté serveur appliquée aussi).
      const poi = { name };
      if (form.address.trim()) poi.address = form.address.trim().slice(0, 300);
      if (form.place_id.trim()) poi.place_id = form.place_id.trim();
      const latNum = parseFloat(form.lat);
      const lngNum = parseFloat(form.lng);
      if (Number.isFinite(latNum)) poi.lat = latNum;
      if (Number.isFinite(lngNum)) poi.lng = lngNum;
      const ratingNum = parseFloat(form.rating);
      if (Number.isFinite(ratingNum)) poi.rating = ratingNum;
      const ratingsNum = parseInt(form.user_ratings_total, 10);
      if (Number.isFinite(ratingsNum)) poi.user_ratings_total = ratingsNum;
      if (form.photo_reference.trim())
        poi.photo_reference = form.photo_reference.trim();
      if (form.website.trim()) poi.website = form.website.trim().slice(0, 500);
      if (form.phone.trim()) poi.phone = form.phone.trim().slice(0, 200);
      if (form.tisseo_url.trim())
        poi.tisseo_url = form.tisseo_url.trim().slice(0, 500);

      const headers = getAuthHeaders ? await getAuthHeaders() : {};
      const { ok, status, json } = await _apiCall(
        "livret_alentours_poi_add_manual",
        {
          method: "POST",
          body: {
            logement_id,
            section_type: "alentours",
            key: sectionKey,
            poi,
          },
          headers,
        },
      );
      if (!ok) {
        if (status === 409 && json.error === "duplicate_place_id") {
          setSubmitError(
            `Ce POI Google existe déjà dans cette catégorie. Pour le ré-afficher, utiliser Épingler depuis sa card.`,
          );
        } else {
          setSubmitError(json.message || json.error || `HTTP ${status}`);
        }
        return;
      }
      onAdded?.({ manual_id: json.manual_id, row: json.row });
      onClose?.();
    } catch (err) {
      setSubmitError(err.message || "Erreur réseau");
    } finally {
      setSubmitting(false);
    }
  }

  // ────────────────────────────────────────────────────────────────────────
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-poi-modal-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fbz-modal-sheet"
        style={{
          background: DS.white,
          borderRadius: 12,
          padding: 20,
          maxWidth: 600,
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          fontFamily: DS.fontBody,
        }}
      >
        <h3
          id="add-poi-modal-title"
          style={{
            margin: 0,
            marginBottom: 12,
            fontFamily: DS.fontDisplay,
            color: DS.navy,
            fontSize: 18,
          }}
        >
          ➕ Ajouter manuellement un POI (catégorie {sectionKey})
        </h3>

        {/* Tabs mode */}
        <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
          <button
            type="button"
            onClick={() => setMode("search")}
            disabled={sectionKey === "transports"}
            aria-pressed={mode === "search"}
            title={
              sectionKey === "transports"
                ? "Recherche Google indisponible pour transports (D9 — saisie Tisséo libre)"
                : "Rechercher Google Places"
            }
            style={_tabStyle(mode === "search", sectionKey === "transports")}
          >
            🔍 Rechercher Google
          </button>
          <button
            type="button"
            onClick={() => setMode("manual")}
            aria-pressed={mode === "manual"}
            style={_tabStyle(mode === "manual", false)}
          >
            ✏️ Saisie libre
          </button>
        </div>

        {mode === "search" && (
          <div>
            <form
              onSubmit={handleSearch}
              style={{ display: "flex", gap: 8, marginBottom: 12 }}
            >
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Nom ou adresse (ex. Apulian Toulouse)"
                maxLength={200}
                aria-label="Termes de recherche Google Places"
                style={_inputStyle()}
                disabled={searching || retryAfter > 0}
              />
              <button
                type="submit"
                disabled={searching || retryAfter > 0 || !query.trim()}
                style={_btnPrimaryStyle(searching || retryAfter > 0)}
              >
                {searching
                  ? "⏳"
                  : retryAfter > 0
                    ? `${retryAfter}s`
                    : "🔍 Rechercher"}
              </button>
            </form>

            {searchError && (
              <div
                role="alert"
                aria-live="polite"
                style={_errorBoxStyle()}
              >
                {searchError}
              </div>
            )}

            <div aria-live="polite">
              {candidates.length > 0 && (
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {candidates.map((c) => (
                    <li key={c.place_id || c.name} style={{ marginBottom: 8 }}>
                      <button
                        type="button"
                        onClick={() => handleSelectCandidate(c)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          background: "#f8fafd",
                          border: `1px solid ${DS.border}`,
                          borderRadius: 8,
                          padding: 10,
                          cursor: "pointer",
                          fontFamily: DS.fontBody,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            color: DS.navy,
                            fontSize: 13,
                          }}
                        >
                          {c.name}
                        </div>
                        {c.address && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#666",
                              marginTop: 2,
                            }}
                          >
                            {c.address}
                          </div>
                        )}
                        {typeof c.rating === "number" && c.rating > 0 && (
                          <div
                            style={{
                              fontSize: 11,
                              color: DS.skyDeep,
                              marginTop: 2,
                            }}
                          >
                            ⭐ {c.rating}{" "}
                            {c.user_ratings_total > 0 && (
                              <span style={{ color: "#888" }}>
                                ({c.user_ratings_total})
                              </span>
                            )}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {mode === "manual" && (
          <form onSubmit={handleSubmit}>
            <Field
              label="Nom *"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              maxLength={200}
              required
            />
            <Field
              label="Adresse"
              value={form.address}
              onChange={(v) => setForm({ ...form, address: v })}
              maxLength={300}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <Field
                label="Latitude"
                value={form.lat}
                onChange={(v) => setForm({ ...form, lat: v })}
                placeholder="43.6047"
                style={{ flex: 1 }}
              />
              <Field
                label="Longitude"
                value={form.lng}
                onChange={(v) => setForm({ ...form, lng: v })}
                placeholder="1.4442"
                style={{ flex: 1 }}
              />
            </div>
            {sectionKey === "transports" && (
              <Field
                label="URL Tisséo (temps réel)"
                value={form.tisseo_url}
                onChange={(v) => setForm({ ...form, tisseo_url: v })}
                placeholder="https://tisseo.fr/..."
                maxLength={500}
              />
            )}
            {sectionKey !== "transports" && (
              <>
                <Field
                  label="Site web"
                  value={form.website}
                  onChange={(v) => setForm({ ...form, website: v })}
                  placeholder="https://..."
                  maxLength={500}
                />
                <Field
                  label="Téléphone"
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                  maxLength={200}
                />
              </>
            )}

            {submitError && (
              <div role="alert" style={_errorBoxStyle()}>
                {submitError}
              </div>
            )}

            <div
              style={{
                marginTop: 14,
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                style={_btnSecondaryStyle()}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={submitting || !form.name.trim()}
                style={_btnPrimaryStyle(submitting)}
              >
                {submitting ? "⏳ Envoi…" : "💾 Valider l'ajout"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  required,
  style,
}) {
  return (
    <label style={{ display: "block", marginBottom: 10, ...(style || {}) }}>
      <span
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 700,
          color: "#444",
          marginBottom: 4,
          fontFamily: DS.fontBody,
        }}
      >
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        required={required}
        aria-label={label}
        style={_inputStyle()}
      />
    </label>
  );
}

function _tabStyle(active, disabled) {
  return {
    padding: "8px 12px",
    background: active ? DS.navy : DS.white,
    color: active ? DS.white : DS.navy,
    border: `1px solid ${DS.navy}`,
    borderRadius: 7,
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: DS.fontBody,
    opacity: disabled ? 0.5 : 1,
  };
}

function _inputStyle() {
  return {
    width: "100%",
    padding: "8px 10px",
    border: `1px solid ${DS.border}`,
    borderRadius: 7,
    fontSize: 13,
    fontFamily: DS.fontBody,
    boxSizing: "border-box",
  };
}

function _btnPrimaryStyle(busy) {
  return {
    padding: "8px 14px",
    background: DS.navy,
    color: DS.white,
    border: "none",
    borderRadius: 7,
    cursor: busy ? "wait" : "pointer",
    fontFamily: DS.fontBody,
    fontSize: 13,
    fontWeight: 600,
    minHeight: 36,
    opacity: busy ? 0.6 : 1,
  };
}

function _btnSecondaryStyle() {
  return {
    padding: "8px 14px",
    background: DS.white,
    color: "#666",
    border: `1px solid ${DS.border}`,
    borderRadius: 7,
    cursor: "pointer",
    fontFamily: DS.fontBody,
    fontSize: 13,
    minHeight: 36,
  };
}

function _errorBoxStyle() {
  return {
    padding: 10,
    marginBottom: 10,
    background: "#fef2f2",
    border: `1px solid ${DS.charge}`,
    borderRadius: 7,
    color: DS.charge,
    fontSize: 12,
    fontFamily: DS.fontBody,
  };
}
